/**
 * ArticlesTable - Tableau des articles générés par la surveillance
 * Extrait de VeillePage.js pour améliorer la maintenabilité
 */
import React from 'react';
import { 
  ExternalLink, Filter, Loader2, Globe, Send, 
  FileText, Eye, Zap, RotateCcw, Trash2, ScanSearch 
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { TypeBadge } from './UIComponents';

/**
 * Ligne d'article dans le tableau
 */
const ArticleRow = ({
  article,
  wpSites,
  scrapingId,
  generatingId,
  publishingId,
  onScrape,
  onGenerate,
  onReview,
  onPublish,
  onReset,
  onDelete
}) => {
  // Déterminer le statut et le style
  const getPublishInfo = () => {
    if (article.publish_status === 'published') {
      return { status: 'Publié', statusClass: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' };
    } else if (article.publish_status === 'draft') {
      return { status: 'Brouillon', statusClass: 'bg-amber-500/20 text-amber-400 border-amber-500/30' };
    } else if (article.generated_article) {
      return { status: 'Généré', statusClass: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' };
    } else if (article.scraped_content) {
      return { status: 'Scrapé', statusClass: 'bg-purple-500/20 text-purple-400 border-purple-500/30' };
    }
    return { status: 'Nouveau', statusClass: 'bg-slate-500/20 text-slate-400 border-slate-500/30' };
  };
  
  const publishInfo = getPublishInfo();
  
  // Récupérer le site de publication
  const getPublishedSite = () => {
    if (article.published_site_id) {
      const site = wpSites.find(s => s.id === article.published_site_id);
      return site?.name || article.published_site_name || '-';
    }
    if (article.target_sites?.length > 0) {
      const site = wpSites.find(s => s.id === article.target_sites[0]);
      return site?.name || '-';
    }
    return '-';
  };
  
  // Récupérer le titre de l'article généré/publié
  const getGeneratedTitle = () => {
    if (article.published_title) return article.published_title;
    if (article.generated_article?.title) return article.generated_article.title;
    if (article.generated_articles) {
      const firstSite = Object.values(article.generated_articles)[0];
      if (firstSite?.title) return firstSite.title;
    }
    return null;
  };
  
  const generatedTitle = getGeneratedTitle();
  const publishedSite = getPublishedSite();
  
  // Formater la date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  };

  return (
    <div 
      className="grid grid-cols-12 gap-2 px-3 py-2.5 bg-slate-900/30 border border-slate-800 rounded hover:border-slate-600 transition-all items-center"
      data-testid={`article-${article.id}`}
    >
      {/* Source */}
      <div className="col-span-2 flex items-center gap-1.5 min-w-0">
        <TypeBadge type={article.source_type} />
        <span className="text-xs text-slate-400 truncate" title={article.source_name}>
          {article.source_name || '-'}
        </span>
      </div>
      
      {/* Date */}
      <div className="col-span-1">
        <span className="text-xs text-slate-500 font-mono">
          {formatDate(article.published_at || article.created_at)}
        </span>
      </div>
      
      {/* Statut */}
      <div className="col-span-1">
        <span className={`text-[10px] px-2 py-0.5 rounded border font-medium ${publishInfo.statusClass}`}>
          {publishInfo.status}
        </span>
      </div>
      
      {/* Site cible */}
      <div className="col-span-2">
        <span className="text-xs text-slate-400 truncate block" title={publishedSite}>
          {publishedSite}
        </span>
      </div>
      
      {/* Titre article */}
      <div className="col-span-3 min-w-0 overflow-hidden">
        {generatedTitle ? (
          <div className="flex items-center gap-1">
            {article.published_url ? (
              <a 
                href={article.published_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-emerald-400 hover:text-emerald-300 truncate flex items-center gap-1"
                title={generatedTitle}
              >
                <span className="truncate">{generatedTitle}</span>
                <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </a>
            ) : (
              <span className="text-xs text-cyan-400 truncate block" title={generatedTitle}>
                {generatedTitle}
              </span>
            )}
          </div>
        ) : (
          <a
            href={article.link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-slate-400 hover:text-slate-300 truncate flex items-center gap-1"
            title={article.title}
          >
            <span className="truncate">{article.title}</span>
            <ExternalLink className="w-3 h-3 flex-shrink-0 text-slate-600" />
          </a>
        )}
      </div>
      
      {/* Actions */}
      <div className="col-span-3 flex items-center justify-end gap-1 flex-shrink-0">
        {!article.scraped_content && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onScrape(article.id)}
            disabled={scrapingId === article.id}
            className="h-6 px-1.5 text-xs border-slate-600 text-slate-300"
            title="Scraper"
            data-testid={`scrape-btn-${article.id}`}
          >
            {scrapingId === article.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
          </Button>
        )}
        
        {article.scraped_content && !article.generated_article && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onGenerate(article.id)}
            disabled={generatingId === article.id}
            className="h-6 px-1.5 text-xs border-amber-500/50 text-amber-400"
            title="Générer"
            data-testid={`generate-btn-${article.id}`}
          >
            {generatingId === article.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
          </Button>
        )}
        
        {(article.scraped_content || article.generated_article) && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onReview(article.id)}
            className="h-6 px-1.5 text-xs border-cyan-500/50 text-cyan-400"
            title="Voir"
            data-testid={`review-btn-${article.id}`}
          >
            <Eye className="w-3 h-3" />
          </Button>
        )}
        
        {article.generated_article && !article.publish_status && (
          <>
            <Button
              size="sm"
              onClick={() => onPublish(article.id, 'draft')}
              disabled={publishingId === article.id}
              className="h-6 px-1.5 text-xs bg-amber-600/80 hover:bg-amber-600 text-white"
              title="Brouillon"
              data-testid={`draft-btn-${article.id}`}
            >
              {publishingId === article.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            </Button>
            <Button
              size="sm"
              onClick={() => onPublish(article.id, 'publish')}
              disabled={publishingId === article.id}
              className="h-6 px-1.5 text-xs bg-emerald-600/80 hover:bg-emerald-600 text-white"
              title="Publier"
              data-testid={`publish-btn-${article.id}`}
            >
              {publishingId === article.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Globe className="w-3 h-3" />}
            </Button>
          </>
        )}
        
        {article.generated_article && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onReset(article.id)}
            className="h-6 px-1.5 text-xs border-orange-500/50 text-orange-400"
            title="Reset"
            data-testid={`reset-btn-${article.id}`}
          >
            <RotateCcw className="w-3 h-3" />
          </Button>
        )}
        
        <Button
          size="sm"
          variant="outline"
          onClick={() => onDelete(article.id)}
          className="h-6 px-1.5 text-xs border-red-500/30 text-red-400 hover:bg-red-900/20"
          title="Supprimer"
          data-testid={`delete-article-btn-${article.id}`}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
};

/**
 * Composant principal : Tableau des articles
 */
const ArticlesTable = ({
  articles,
  sources,
  wpSites,
  loading,
  daysFilter,
  setDaysFilter,
  matchedOnly,
  setMatchedOnly,
  scrapingId,
  generatingId,
  publishingId,
  onScrape,
  onGenerate,
  onReview,
  onPublish,
  onReset,
  onDelete
}) => {
  return (
    <div className="lg:col-span-3">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-cyan-400 font-mono text-sm uppercase tracking-wider">
            Articles 
            <span className="text-slate-500 ml-1">({articles.length})</span>
          </h2>
          
          {/* Filtre période */}
          <select
            value={daysFilter}
            onChange={(e) => setDaysFilter(Number(e.target.value))}
            className="text-xs bg-slate-900 border border-slate-700 rounded px-2 py-1 text-slate-300 font-mono"
          >
            <option value={7}>7 jours</option>
            <option value={14}>14 jours</option>
            <option value={30}>30 jours</option>
          </select>
        </div>
        
        <button
          onClick={() => setMatchedOnly(!matchedOnly)}
          className={`flex items-center gap-1 text-xs font-mono px-3 py-1.5 rounded border transition-all ${
            matchedOnly 
              ? 'bg-amber-500/10 border-amber-500/50 text-amber-400' 
              : 'bg-slate-900/50 border-slate-700 text-slate-400'
          }`}
          data-testid="toggle-matched-only"
        >
          <Filter className="w-3 h-3" />
          {matchedOnly ? 'Mots-clés uniquement' : 'Tous'}
        </button>
      </div>

      <div className="space-y-1">
        {loading ? (
          <p className="text-slate-500 font-mono text-center py-8">Chargement...</p>
        ) : articles.length === 0 ? (
          <Card className="bg-slate-900/30 border-slate-800">
            <CardContent className="py-12 text-center">
              <ScanSearch className="w-10 h-10 text-slate-700 mx-auto mb-3" />
              <p className="text-slate-500 font-mono text-sm">
                {sources.length === 0 
                  ? 'Ajoutez une source pour commencer' 
                  : matchedOnly 
                    ? 'Aucun article correspondant aux mots-clés' 
                    : `Aucun article ces ${daysFilter} derniers jours`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* En-tête du tableau */}
            <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-800">
              <div className="col-span-2">Source</div>
              <div className="col-span-1">Date</div>
              <div className="col-span-1">Statut</div>
              <div className="col-span-2">Site cible</div>
              <div className="col-span-3">Titre article</div>
              <div className="col-span-3 text-right">Actions</div>
            </div>
            
            {/* Liste des articles */}
            {articles.map(article => (
              <ArticleRow
                key={article.id}
                article={article}
                wpSites={wpSites}
                scrapingId={scrapingId}
                generatingId={generatingId}
                publishingId={publishingId}
                onScrape={onScrape}
                onGenerate={onGenerate}
                onReview={onReview}
                onPublish={onPublish}
                onReset={onReset}
                onDelete={onDelete}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default ArticlesTable;
